/**
 * Sprint D1 · Directorio Freemium — Panel SuperAdmin.
 *
 * Sub-tabs:
 *  - Cola de revisión: aprobar / editar / rechazar perfiles importados.
 *  - Importar CSV: preview (sin escribir) → confirmar importación.
 *  - Historial: batches de importación.
 *
 * Regla: ningún perfil se publica sin pasar por "Aprobar".
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { api, formatErr } from "../../lib/api";
import { Button } from "../../components/ui/button";
import { Input } from "../../components/ui/input";
import { toast } from "sonner";
import {
  Storefront, UploadSimple, CheckCircle, XCircle, PencilSimple, MagnifyingGlass,
  WarningCircle, ClockCounterClockwise, X, MapPin, Phone, Globe, ListChecks,
  Binoculars, Sparkle, Buildings, ShieldCheck, Trash, Prohibit,
} from "@phosphor-icons/react";

const STATUS_META = {
  pending_review: { label: "Pendiente", cls: "bg-[var(--ag-gold)]/15 text-[var(--ag-gold)] border-[var(--ag-gold)]/40" },
  published: { label: "Publicado", cls: "bg-emerald-500/15 text-emerald-300 border-emerald-500/30" },
  rejected: { label: "Rechazado", cls: "bg-red-500/15 text-red-300 border-red-500/30" },
  merged: { label: "Fusionado", cls: "bg-zinc-500/15 text-zinc-300 border-zinc-500/30" },
};

function ConfidenceBadge({ value }) {
  const cls = value >= 70 ? "text-emerald-300 border-emerald-500/40" : value >= 40 ? "text-[var(--ag-gold)] border-[var(--ag-gold)]/40" : "text-red-300 border-red-500/40";
  return <span className={`text-[10px] px-2 py-0.5 rounded-full border tabular-nums ${cls}`}>{value}%</span>;
}

export default function DirectoryPanel() {
  const [subtab, setSubtab] = useState("review");
  const [stats, setStats] = useState(null);

  const loadStats = useCallback(() => {
    api.get("/superadmin/directory/stats").then(({ data }) => setStats(data)).catch(() => {});
  }, []);
  useEffect(() => { loadStats(); }, [loadStats]);

  return (
    <div className="space-y-5" data-testid="sa-directory-panel">
      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        {[
          ["Pendientes", stats?.pending_review, "text-[var(--ag-gold)]"],
          ["Publicados", stats?.published, "text-emerald-300"],
          ["Rechazados", stats?.rejected, "text-red-300"],
          ["Posibles duplicados", stats?.flagged_duplicates, "text-orange-300"],
          ["Total freemium", stats?.total_freemium, "text-[var(--ag-text)]"],
        ].map(([label, val, cls]) => (
          <div key={label} className="ag-card p-4" data-testid={`sa-dir-stat-${label}`}>
            <div className="text-[10px] uppercase tracking-[0.2em] text-[var(--ag-text-dim)]">{label}</div>
            <div className={`mt-2 font-display font-bold text-2xl tabular-nums ${cls}`}>{val ?? "—"}</div>
          </div>
        ))}
      </div>

      {/* Sub-tabs */}
      <div className="flex items-center gap-2 border-b border-[var(--ag-border)] overflow-x-auto">
        {[
          ["review", "Cola de revisión", ListChecks],
          ["discover", "Descubrir (OSM)", Binoculars],
          ["rasic", "Descubrir (RASIC)", Buildings],
          ["import", "Importar CSV", UploadSimple],
          ["batches", "Historial", ClockCounterClockwise],
        ].map(([key, label, Icon]) => (
          <button key={key} onClick={() => setSubtab(key)}
            className={`px-4 py-2.5 text-sm font-semibold border-b-2 -mb-px transition-colors whitespace-nowrap ${subtab === key ? "border-[var(--ag-gold)] text-[var(--ag-text)]" : "border-transparent text-[var(--ag-text-dim)] hover:text-white"}`}
            data-testid={`sa-dir-subtab-${key}`}>
            <Icon size={14} className="inline mr-1.5" /> {label}
          </button>
        ))}
      </div>

      {subtab === "review" && <ReviewQueue onChanged={loadStats} />}
      {subtab === "discover" && <DiscoverTab onImported={() => { loadStats(); setSubtab("review"); }} />}
      {subtab === "rasic" && <RasicTab onImported={() => { loadStats(); setSubtab("review"); }} />}
      {subtab === "import" && <ImportCsv onImported={() => { loadStats(); setSubtab("review"); }} />}
      {subtab === "batches" && <BatchesList />}
    </div>
  );
}

// ---------------------------------------------------------------- review ---
function ReviewQueue({ onChanged }) {
  const [status, setStatus] = useState("pending_review");
  const [city, setCity] = useState("");
  const [q, setQ] = useState("");
  const [sector, setSector] = useState("");
  const [subcategory, setSubcategory] = useState("");
  const [sectorStats, setSectorStats] = useState(null); // {total, sectors: [...]}
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // org en edición
  const [dupOrg, setDupOrg] = useState(null);   // org cuyo modal de duplicados está abierto
  const [enriching, setEnriching] = useState(null); // id en proceso de enrich IA
  const [selected, setSelected] = useState(() => new Set()); // ids seleccionados (bulk ops)
  const [bulkBusy, setBulkBusy] = useState(false);

  // Carga el desglose por sector/subsector cada vez que cambian status o ciudad
  const loadSectorStats = useCallback(async () => {
    try {
      const params = new URLSearchParams({ status });
      if (city.trim()) params.set("city", city.trim());
      const { data } = await api.get(`/superadmin/directory/sectors-stats?${params}`);
      setSectorStats(data);
    } catch { /* silent */ }
  }, [status, city]);
  useEffect(() => { loadSectorStats(); }, [loadSectorStats]);

  // Resetea subsector si el sector cambia (cascada) — manejado en onChange del select.

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = new URLSearchParams({ status, limit: "100" });
      if (city.trim()) params.set("city", city.trim());
      if (q.trim()) params.set("q", q.trim());
      if (sector) params.set("sector", sector);
      if (subcategory) params.set("subcategory", subcategory);
      const { data } = await api.get(`/superadmin/directory/review?${params}`);
      setItems(data.items || []);
      setTotal(data.total || 0);
      setSelected(new Set()); // reset selection cuando se recargan los items
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error cargando la cola"); }
    finally { setLoading(false); }
  }, [status, city, q, sector, subcategory]);
  useEffect(() => { load(); }, [load]);

  // Selección múltiple
  const toggleOne = (id) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };
  const allVisibleIds = items.map((o) => o.id);
  const allSelected = allVisibleIds.length > 0 && allVisibleIds.every((id) => selected.has(id));
  const someSelected = !allSelected && allVisibleIds.some((id) => selected.has(id));
  const toggleAllVisible = () => {
    setSelected((prev) => {
      if (allSelected) {
        const next = new Set(prev);
        allVisibleIds.forEach((id) => next.delete(id));
        return next;
      }
      const next = new Set(prev);
      allVisibleIds.forEach((id) => next.add(id));
      return next;
    });
  };

  const bulkReject = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const reason = window.prompt(`Vas a marcar ${ids.length} perfiles como Rechazados (operación reversible).\nMotivo (opcional):`) ?? null;
    if (reason === null) return; // cancel
    setBulkBusy(true);
    try {
      const { data } = await api.post("/superadmin/directory/bulk-reject", { ids, reason });
      toast.success(`${data.rejected} perfiles rechazados`, {
        description: data.requested > data.rejected ? `${data.requested - data.rejected} no aplicables (no freemium o no existen).` : undefined,
      });
      setSelected(new Set());
      load(); loadSectorStats(); onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error rechazando en bloque"); }
    finally { setBulkBusy(false); }
  };

  const bulkDelete = async () => {
    const ids = Array.from(selected);
    if (!ids.length) return;
    const ok = window.confirm(
      `⚠️ Vas a ELIMINAR DEFINITIVAMENTE ${ids.length} perfiles del directorio.\n\n` +
      "Esta acción NO se puede deshacer. Sólo se eliminan perfiles freemium (nunca clientes con cuenta).\n\n" +
      "¿Continuar?"
    );
    if (!ok) return;
    setBulkBusy(true);
    try {
      const { data } = await api.post("/superadmin/directory/bulk-delete", { ids });
      toast.success(`${data.deleted} perfiles eliminados`, {
        description: data.skipped_non_freemium > 0 ? `${data.skipped_non_freemium} omitidos (no eran freemium).` : undefined,
      });
      setSelected(new Set());
      load(); loadSectorStats(); onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error eliminando en bloque"); }
    finally { setBulkBusy(false); }
  };

  const currentSector = (sectorStats?.sectors || []).find((s) => s.id === sector);
  const availableSubsectors = currentSector?.subsectors || [];
  // Sectores con datos primero, luego el resto en gris
  const sectorsWithCount = (sectorStats?.sectors || []).filter((s) => s.count > 0);
  const sectorsEmpty = (sectorStats?.sectors || []).filter((s) => s.count === 0);

  const approve = async (org) => {
    try {
      await api.post(`/superadmin/directory/${org.id}/approve`);
      toast.success(`«${org.name}» publicado → /centro/${org.slug}`);
      load(); loadSectorStats(); onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error al aprobar"); }
  };
  const reject = async (org) => {
    const reason = window.prompt(`Motivo del rechazo de «${org.name}» (opcional):`) ?? null;
    if (reason === null) return;
    try {
      await api.post(`/superadmin/directory/${org.id}/reject`, { reason });
      toast.success(`«${org.name}» rechazado`);
      load(); loadSectorStats(); onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error al rechazar"); }
  };

  const enrich = async (org) => {
    setEnriching(org.id);
    try {
      await api.post(`/superadmin/directory/${org.id}/enrich`);
      toast.success(`SEO generado con IA para «${org.name}»`);
      load();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error de IA"); }
    finally { setEnriching(null); }
  };

  return (
    <div className="space-y-4">
      {/* Filtros */}
      <div className="flex flex-wrap items-center gap-2">
        <select value={status} onChange={(e) => setStatus(e.target.value)}
          className="h-10 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
          data-testid="sa-dir-filter-status">
          <option value="pending_review">Pendientes</option>
          <option value="published">Publicados</option>
          <option value="rejected">Rechazados</option>
          <option value="">Todos</option>
        </select>
        <Input value={city} onChange={(e) => setCity(e.target.value)} placeholder="Ciudad…" className="h-10 w-40" data-testid="sa-dir-filter-city" />
        <select value={sector} onChange={(e) => { setSector(e.target.value); setSubcategory(""); }}
          className="h-10 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm max-w-[14rem] truncate"
          data-testid="sa-dir-filter-sector">
          <option value="">Sector — todos ({sectorStats?.total ?? "…"})</option>
          {sectorsWithCount.map((s) => (
            <option key={s.id} value={s.id}>{s.label_es} ({s.count})</option>
          ))}
          {sectorsEmpty.length > 0 && <option disabled>──────────</option>}
          {sectorsEmpty.map((s) => (
            <option key={s.id} value={s.id} disabled>{s.label_es} (0)</option>
          ))}
        </select>
        <select value={subcategory} onChange={(e) => setSubcategory(e.target.value)}
          disabled={!sector || availableSubsectors.length === 0}
          className="h-10 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm disabled:opacity-40 max-w-[14rem] truncate"
          data-testid="sa-dir-filter-subcategory">
          <option value="">{sector ? (availableSubsectors.length ? `Subsector — todos (${currentSector?.count ?? 0})` : "Sin subsectores") : "Subsector"}</option>
          {availableSubsectors.map((sub) => (
            <option key={sub.key} value={sub.key}>{sub.label_es} ({sub.count})</option>
          ))}
        </select>
        <div className="relative flex-1 max-w-xs">
          <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[var(--ag-text-dim)]" />
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Nombre, teléfono, slug…" className="pl-9 h-10" data-testid="sa-dir-filter-q" />
        </div>
        {(sector || subcategory || q || city) && (
          <button onClick={() => { setSector(""); setSubcategory(""); setQ(""); setCity(""); }}
            className="h-10 px-2.5 text-xs text-[var(--ag-text-dim)] hover:text-white inline-flex items-center gap-1"
            data-testid="sa-dir-filter-clear" title="Limpiar filtros">
            <X size={12} /> limpiar
          </button>
        )}
        <span className="text-xs text-[var(--ag-text-dim)] ml-auto tabular-nums" data-testid="sa-dir-review-total">{total} perfiles</span>
      </div>

      {/* Barra acciones bulk — visible cuando hay selección */}
      {selected.size > 0 && (
        <div className="flex items-center gap-2 px-4 py-2.5 rounded-sm border border-[var(--ag-gold)]/40 bg-[var(--ag-gold)]/10 flex-wrap"
             data-testid="sa-dir-bulk-bar">
          <span className="text-sm font-semibold text-white">
            <span className="tabular-nums">{selected.size}</span> perfil{selected.size === 1 ? "" : "es"} seleccionado{selected.size === 1 ? "" : "s"}
          </span>
          <button onClick={() => setSelected(new Set())}
            className="text-[11px] text-[var(--ag-text-dim)] hover:text-white inline-flex items-center gap-1"
            data-testid="sa-dir-bulk-clear">
            <X size={11} /> deseleccionar
          </button>
          <div className="flex-1" />
          <button onClick={bulkReject} disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm border border-orange-500/50 text-orange-300 hover:bg-orange-500/15 text-xs font-semibold uppercase tracking-wider disabled:opacity-40"
            data-testid="sa-dir-bulk-reject">
            <Prohibit size={13} weight="bold" /> Rechazar {selected.size}
          </button>
          <button onClick={bulkDelete} disabled={bulkBusy}
            className="inline-flex items-center gap-1.5 h-9 px-4 rounded-sm border border-red-500/60 text-red-300 hover:bg-red-500/20 text-xs font-semibold uppercase tracking-wider disabled:opacity-40"
            data-testid="sa-dir-bulk-delete">
            <Trash size={13} weight="bold" /> Eliminar {selected.size}
          </button>
        </div>
      )}

      {/* Tabla */}
      <div className="ag-card overflow-x-auto">
        {loading ? (
          <div className="p-8 text-center text-sm text-[var(--ag-text-dim)]">Cargando…</div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center" data-testid="sa-dir-review-empty">
            <Storefront size={36} weight="duotone" className="text-[var(--ag-text-dim)] mx-auto" />
            <p className="text-sm text-[var(--ag-text-dim)] mt-3">No hay perfiles con estos filtros. Importa un CSV para empezar.</p>
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead>
              <tr className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
                <th className="px-3 py-3 w-10">
                  <input
                    type="checkbox"
                    checked={allSelected}
                    ref={(el) => { if (el) el.indeterminate = someSelected; }}
                    onChange={toggleAllVisible}
                    className="w-4 h-4 cursor-pointer accent-[var(--ag-gold)]"
                    aria-label={allSelected ? "Deseleccionar todos" : "Seleccionar todos los visibles"}
                    data-testid="sa-dir-select-all"
                  />
                </th>
                <th className="text-left px-4 py-3">Negocio</th>
                <th className="text-left px-4 py-3 hidden md:table-cell">Ciudad</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Teléfono</th>
                <th className="text-left px-4 py-3 hidden lg:table-cell">Fuente</th>
                <th className="text-center px-2 py-3">Conf.</th>
                <th className="text-center px-2 py-3">Estado</th>
                <th className="text-right px-4 py-3">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {items.map((o) => {
                const sm = STATUS_META[o.listing_status] || STATUS_META.pending_review;
                const isChecked = selected.has(o.id);
                return (
                  <tr key={o.id} className={`border-b border-[var(--ag-border)]/50 hover:bg-[var(--ag-midnight-soft)]/40 ${isChecked ? "bg-[var(--ag-gold)]/5" : ""}`} data-testid={`sa-dir-row-${o.slug || o.id}`}>
                    <td className="px-3 py-3 align-top">
                      <input
                        type="checkbox"
                        checked={isChecked}
                        onChange={() => toggleOne(o.id)}
                        className="w-4 h-4 cursor-pointer accent-[var(--ag-gold)]"
                        aria-label={`Seleccionar ${o.name}`}
                        data-testid={`sa-dir-row-select-${o.id}`}
                      />
                    </td>
                    <td className="px-4 py-3">
                      <div className="font-semibold flex items-center gap-1.5 flex-wrap">
                        {o.name}
                        {o.verification_status === "rasic_verified" && (
                          <span title="Verificado en RASIC (Registre d'Agents de la Seguretat Industrial · Generalitat)"
                                className="text-[9px] px-1.5 py-0.5 rounded-sm bg-emerald-500/15 border border-emerald-500/40 text-emerald-300 inline-flex items-center gap-0.5"
                                data-testid={`sa-dir-rasic-badge-${o.id}`}>
                            <ShieldCheck size={9} weight="fill" /> RASIC
                          </span>
                        )}
                        {o.seo?.ai_generated && (
                          <span title={`SEO IA: ${o.seo?.meta_title || ""}`} data-testid={`sa-dir-ai-badge-${o.id}`}>
                            <Sparkle size={13} weight="fill" className="text-[var(--ag-gold)]" />
                          </span>
                        )}
                        {(o.possible_duplicate_of?.length > 0) && (
                          <button title="Ver posibles duplicados (IA)" onClick={() => setDupOrg(o)} data-testid={`sa-dir-dup-flag-${o.id}`}>
                            <WarningCircle size={14} weight="fill" className="text-orange-400 hover:scale-125 transition-transform" />
                          </button>
                        )}
                      </div>
                      <div className="text-[11px] text-[var(--ag-text-dim)]">{(o.sectors || []).join(" · ") || "sin sector"}{o.slug ? ` · /${o.slug}` : ""}{o.tax_id ? ` · ${o.tax_id}` : ""}</div>
                    </td>
                    <td className="px-4 py-3 hidden md:table-cell text-[var(--ag-text-dim)]">{[o.city, o.province].filter(Boolean).join(", ")}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[var(--ag-text-dim)] tabular-nums">{o.phone || "—"}</td>
                    <td className="px-4 py-3 hidden lg:table-cell text-[var(--ag-text-dim)] text-[11px]">{o.source?.name || o.source?.type || "—"}</td>
                    <td className="px-2 py-3 text-center"><ConfidenceBadge value={o.confidence_score || 0} /></td>
                    <td className="px-2 py-3 text-center"><span className={`text-[10px] px-2 py-0.5 rounded-full border ${sm.cls}`}>{sm.label}</span></td>
                    <td className="px-4 py-3">
                      <div className="flex items-center justify-end gap-1.5">
                        {!o.seo?.ai_generated && (
                          <button onClick={() => enrich(o)} disabled={enriching === o.id} title="Generar SEO con IA"
                            className="w-8 h-8 grid place-items-center rounded-sm border border-[var(--ag-gold)]/40 text-[var(--ag-gold)] hover:bg-[var(--ag-gold)]/15 disabled:opacity-40"
                            data-testid={`sa-dir-enrich-${o.id}`}>
                            <Sparkle size={14} weight={enriching === o.id ? "fill" : "regular"} className={enriching === o.id ? "animate-pulse" : ""} />
                          </button>
                        )}
                        <button onClick={() => setEditing(o)} title="Editar"
                          className="w-8 h-8 grid place-items-center rounded-sm border border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white hover:border-[var(--ag-gold)]/60"
                          data-testid={`sa-dir-edit-${o.id}`}>
                          <PencilSimple size={14} />
                        </button>
                        {o.listing_status !== "published" && (
                          <button onClick={() => approve(o)} title="Aprobar y publicar"
                            className="w-8 h-8 grid place-items-center rounded-sm border border-emerald-500/40 text-emerald-300 hover:bg-emerald-500/15"
                            data-testid={`sa-dir-approve-${o.id}`}>
                            <CheckCircle size={15} weight="fill" />
                          </button>
                        )}
                        {o.listing_status !== "rejected" && (
                          <button onClick={() => reject(o)} title="Rechazar"
                            className="w-8 h-8 grid place-items-center rounded-sm border border-red-500/40 text-red-300 hover:bg-red-500/15"
                            data-testid={`sa-dir-reject-${o.id}`}>
                            <XCircle size={15} weight="fill" />
                          </button>
                        )}
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>

      {editing && (
        <EditModal org={editing} onClose={() => setEditing(null)} onSaved={() => { setEditing(null); load(); }} />
      )}
      {dupOrg && (
        <DuplicatesModal org={dupOrg} onClose={() => setDupOrg(null)}
          onChanged={() => { setDupOrg(null); load(); onChanged(); }} />
      )}
    </div>
  );
}

// ------------------------------------------------------ duplicados (IA) ----
function DuplicatesModal({ org, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get(`/superadmin/directory/${org.id}/duplicates`)
      .then(({ data }) => setData(data))
      .catch((e) => toast.error(formatErr(e?.response?.data?.detail) || "Error cargando duplicados"))
      .finally(() => setLoading(false));
  }, [org.id]);

  const rejectThis = async () => {
    try {
      await api.post(`/superadmin/directory/${org.id}/reject`, { reason: "Duplicado de un perfil existente" });
      toast.success("Perfil rechazado como duplicado");
      onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error"); }
  };
  const dismiss = async () => {
    try {
      await api.post(`/superadmin/directory/${org.id}/dismiss-duplicates`);
      toast.success("Aviso de duplicado descartado");
      onChanged();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error"); }
  };

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="ag-card max-w-xl w-full max-h-[85vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sa-dir-dup-modal">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ag-border)]">
          <h3 className="font-display font-black text-lg tracking-tight flex items-center gap-2">
            <WarningCircle size={18} weight="fill" className="text-orange-400" /> Posibles duplicados de «{org.name}»
          </h3>
          <button onClick={onClose} className="text-[var(--ag-text-dim)] hover:text-white" aria-label="Cerrar"><X size={18} /></button>
        </div>
        <div className="p-5 space-y-3">
          {loading ? (
            <div className="text-sm text-[var(--ag-text-dim)] flex items-center gap-2">
              <Sparkle size={14} className="animate-pulse text-[var(--ag-gold)]" /> La IA está comparando fichas…
            </div>
          ) : (data?.candidates || []).length === 0 ? (
            <p className="text-sm text-[var(--ag-text-dim)]">No se encontraron candidatos comparables.</p>
          ) : (
            data.candidates.map((c) => (
              <div key={c.id} className="border border-[var(--ag-border)] rounded-sm p-4" data-testid={`sa-dir-dup-candidate-${c.id}`}>
                <div className="flex items-start justify-between gap-3">
                  <div>
                    <div className="font-semibold">{c.name}</div>
                    <div className="text-[11px] text-[var(--ag-text-dim)] mt-0.5">
                      {[c.city, c.address].filter(Boolean).join(" · ")} {c.phone ? `· ${c.phone}` : ""} · {c.account_type === "freemium" ? "freemium" : "registrado"} ({c.listing_status})
                    </div>
                  </div>
                  <span className={`shrink-0 text-[10px] px-2 py-1 rounded-full border font-bold ${c.verdict?.is_duplicate ? "text-red-300 border-red-500/40 bg-red-500/10" : "text-emerald-300 border-emerald-500/40 bg-emerald-500/10"}`}>
                    {c.verdict?.is_duplicate ? `DUPLICADO ${c.verdict.confidence}%` : `Distinto ${100 - (c.verdict?.confidence || 0)}%`}
                  </span>
                </div>
                {c.verdict?.reason && (
                  <p className="text-[12px] text-[var(--ag-text-dim)] mt-2 flex items-start gap-1.5">
                    <Sparkle size={12} weight="fill" className="text-[var(--ag-gold)] mt-0.5 shrink-0" /> {c.verdict.reason}
                  </p>
                )}
              </div>
            ))
          )}
        </div>
        <div className="p-5 border-t border-[var(--ag-border)] flex flex-wrap justify-end gap-2">
          <Button variant="ghost" onClick={dismiss} className="h-10 text-[var(--ag-text-dim)]" data-testid="sa-dir-dup-dismiss">
            Mantener ambos (descartar aviso)
          </Button>
          <Button onClick={rejectThis} className="h-10 px-5 bg-red-500/15 border border-red-500/40 text-red-300 hover:bg-red-500/25" data-testid="sa-dir-dup-reject">
            Rechazar este perfil (es duplicado)
          </Button>
        </div>
      </div>
    </div>
  );
}

// ----------------------------------------------------- descubrir (OSM) -----
function DiscoverTab({ onImported }) {
  const [zone, setZone] = useState("Lleida");
  const [scope, setScope] = useState("province");
  const [enrichAI, setEnrichAI] = useState(true);
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);

  useEffect(() => () => clearInterval(pollRef.current), []);

  const runPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post("/superadmin/directory/discover", { zone, scope, commit: false });
      setPreview(data);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error consultando OpenStreetMap"); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/directory/discover", { zone, scope, commit: true, enrich: enrichAI });
      setJob({ id: data.job_id, status: "running", stage: "iniciando" });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/superadmin/directory/jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done" || j.status === "error") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (j.status === "done") {
              toast.success(`Descubrimiento completado: ${j.created} perfiles nuevos en la cola`);
              onImported();
            } else {
              toast.error(`El job falló: ${j.error || "error desconocido"}`);
            }
          }
        } catch { /* siguiente tick */ }
      }, 2500);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "No se pudo iniciar el descubrimiento");
      setBusy(false);
    }
  };

  const pct = job?.total ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;

  return (
    <div className="space-y-4">
      <div className="ag-card p-5 space-y-4">
        <div className="text-sm text-[var(--ag-text-dim)]">
          Descubre negocios de automoción reales desde <strong className="text-[var(--ag-text)]">OpenStreetMap</strong> (datos
          abiertos, licencia ODbL con atribución). Talleres, detailing, neumáticos, concesionarios, chapa y pintura, ITV y
          talleres de moto. Todo entra <strong className="text-[var(--ag-text)]">pendiente de tu revisión</strong> — nada se publica solo.
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Zona</label>
            <Input value={zone} onChange={(e) => setZone(e.target.value)} placeholder="Lleida, Barcelona…" className="h-10 mt-1 w-48" data-testid="sa-dir-discover-zone" />
          </div>
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Ámbito</label>
            <select value={scope} onChange={(e) => setScope(e.target.value)}
              className="block h-10 mt-1 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
              data-testid="sa-dir-discover-scope">
              <option value="province">Provincia</option>
              <option value="city">Municipio</option>
            </select>
          </div>
          <label className="flex items-center gap-2 h-10 text-sm text-[var(--ag-text-dim)] cursor-pointer select-none">
            <input type="checkbox" checked={enrichAI} onChange={(e) => setEnrichAI(e.target.checked)} className="accent-[var(--ag-gold)]" data-testid="sa-dir-discover-enrich" />
            <Sparkle size={13} weight="fill" className="text-[var(--ag-gold)]" /> Enriquecer con IA (sectores + SEO)
          </label>
          <div className="flex gap-2 ml-auto">
            <Button onClick={runPreview} disabled={busy} className="h-10 px-4 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] hover:border-[var(--ag-gold)]/60" data-testid="sa-dir-discover-preview">
              <Binoculars size={15} className="mr-2" /> Vista previa
            </Button>
            <Button onClick={runImport} disabled={busy} className="ag-btn-gold h-10 px-5" data-testid="sa-dir-discover-import">
              {busy && job ? "Importando…" : "Importar a cola de revisión"}
            </Button>
          </div>
        </div>
      </div>

      {/* Progreso del job */}
      {job && (
        <div className="ag-card p-5" data-testid="sa-dir-discover-job">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold capitalize flex items-center gap-2">
              {job.status === "running" && <Sparkle size={14} weight="fill" className="text-[var(--ag-gold)] animate-pulse" />}
              {job.status === "done" && <CheckCircle size={15} weight="fill" className="text-emerald-300" />}
              {job.status === "error" && <XCircle size={15} weight="fill" className="text-red-300" />}
              {job.stage || job.status}
            </span>
            <span className="text-[var(--ag-text-dim)] tabular-nums">{job.processed || 0}/{job.total || "?"} {job.created ? `· ${job.created} creados` : ""}</span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[var(--ag-midnight)] overflow-hidden">
            <div className="h-full bg-[var(--ag-gold)] transition-all duration-500" style={{ width: `${job.status === "done" ? 100 : pct}%` }} />
          </div>
          {job.status === "done" && job.counts && (
            <div className="mt-3 text-xs text-[var(--ag-text-dim)]">
              ✓ {job.counts.ok + job.counts.flagged_duplicate} importados · ⚑ {job.counts.flagged_duplicate} posibles duplicados ·
              ⊘ {job.counts.skipped_duplicate + (job.counts.skipped_existing || 0)} omitidos · ✕ {job.counts.error} sin datos mínimos
              {typeof job.enrich_errors === "number" && job.enrich_errors > 0 ? ` · IA falló en ${job.enrich_errors}` : ""}
            </div>
          )}
        </div>
      )}

      {/* Vista previa */}
      {preview?.ok && (
        <div className="ag-card p-5 space-y-3" data-testid="sa-dir-discover-preview-report">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <strong>{preview.total_rows}</strong><span className="text-[var(--ag-text-dim)] -ml-2">negocios encontrados en «{preview.zone}»</span>
            <span className="text-emerald-300 tabular-nums">✓ {preview.counts.ok} listos</span>
            <span className="text-orange-300 tabular-nums">⚑ {preview.counts.flagged_duplicate} posibles duplicados</span>
            <span className="text-[var(--ag-text-dim)] tabular-nums">⊘ {preview.counts.skipped_duplicate + (preview.counts.skipped_existing || 0)} ya existentes</span>
            {preview.needs_city_geocode > 0 && (
              <span className="text-[var(--ag-text-dim)] tabular-nums">📍 {preview.needs_city_geocode} sin ciudad (se resolverá por coordenadas al importar)</span>
            )}
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-[var(--ag-border)] rounded-sm">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--ag-midnight-soft)]">
                <tr className="text-[9px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
                  <th className="text-left px-3 py-2">Negocio</th>
                  <th className="text-left px-3 py-2">Ciudad</th>
                  <th className="text-left px-3 py-2">Sectores</th>
                  <th className="text-left px-3 py-2">Teléfono</th>
                  <th className="text-left px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {(preview.results || []).slice(0, 100).map((r) => (
                  <tr key={r.row} className="border-b border-[var(--ag-border)]/40">
                    <td className="px-3 py-1.5">{r.name}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{r.city || "📍 por coordenadas"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{(r.sectors || []).join(", ") || "—"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)] tabular-nums">{r.phone || "—"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{r.status === "ok" ? "✓" : r.status === "error" ? `✕ ${r.reason}` : r.reason}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------- descubrir (RASIC) ----
function RasicTab({ onImported }) {
  const [province, setProvince] = useState("Lleida");
  const [preview, setPreview] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null);
  const pollRef = useRef(null);
  useEffect(() => () => clearInterval(pollRef.current), []);

  const runPreview = async () => {
    setBusy(true); setPreview(null);
    try {
      const { data } = await api.post("/superadmin/directory/import/rasic", { province, commit: false });
      setPreview(data);
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error consultando CKAN RASIC"); }
    finally { setBusy(false); }
  };

  const runImport = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/directory/import/rasic", { province, commit: true });
      setJob({ id: data.job_id, status: "running", stage: "iniciando" });
      pollRef.current = setInterval(async () => {
        try {
          const { data: j } = await api.get(`/superadmin/directory/jobs/${data.job_id}`);
          setJob(j);
          if (j.status === "done" || j.status === "error") {
            clearInterval(pollRef.current);
            setBusy(false);
            if (j.status === "done") {
              toast.success(`RASIC ${province}: ${j.created} nuevos · ${j.merged} fusionados`);
              onImported();
            } else { toast.error(`Job falló: ${j.error || "?"}`); }
          }
        } catch { /* tick */ }
      }, 2000);
    } catch (e) {
      toast.error(formatErr(e?.response?.data?.detail) || "No se pudo iniciar la importación RASIC");
      setBusy(false);
    }
  };

  const pct = job?.total ? Math.min(100, Math.round((job.processed / job.total) * 100)) : 0;

  return (
    <div className="space-y-4">
      <div className="ag-card p-5 space-y-4">
        <div className="text-sm text-[var(--ag-text-dim)]">
          <strong className="text-[var(--ag-text)]">RASIC</strong> · Registre d&apos;Agents de la Seguretat Industrial (Generalitat de Catalunya).
          Listado oficial de talleres legalmente registrados. <strong className="text-[var(--ag-text)]">22.345 talleres</strong> en Catalunya
          (~1.360 en Lleida). Importa NIF, dirección, email, web y especialidades. Sin teléfono (se completa al reclamar).
          Los perfiles entran <strong className="text-[var(--ag-text)]">pending_review</strong> con badge &laquo;RASIC verificado&raquo;.
          Dedupe automático contra OSM por NIF, nombre+ciudad y dirección+ciudad.
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Provincia</label>
            <select value={province} onChange={(e) => setProvince(e.target.value)}
              className="block h-10 mt-1 px-3 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
              data-testid="sa-dir-rasic-province">
              <option value="Lleida">Lleida</option>
              <option value="Girona">Girona</option>
              <option value="Tarragona">Tarragona</option>
              <option value="Barcelona">Barcelona</option>
            </select>
          </div>
          <div className="flex gap-2 ml-auto">
            <Button onClick={runPreview} disabled={busy} className="h-10 px-4 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] hover:border-[var(--ag-gold)]/60" data-testid="sa-dir-rasic-preview">
              <Buildings size={15} className="mr-2" /> Vista previa
            </Button>
            <Button onClick={runImport} disabled={busy} className="ag-btn-gold h-10 px-5" data-testid="sa-dir-rasic-import">
              {busy && job ? "Importando…" : "Importar a cola de revisión"}
            </Button>
          </div>
        </div>
      </div>

      {/* Progreso del job */}
      {job && (
        <div className="ag-card p-5" data-testid="sa-dir-rasic-job">
          <div className="flex items-center justify-between text-sm">
            <span className="font-semibold capitalize flex items-center gap-2">
              {job.status === "running" && <Sparkle size={14} weight="fill" className="text-[var(--ag-gold)] animate-pulse" />}
              {job.status === "done" && <CheckCircle size={15} weight="fill" className="text-emerald-300" />}
              {job.status === "error" && <XCircle size={15} weight="fill" className="text-red-300" />}
              {job.stage || job.status}
            </span>
            <span className="text-[var(--ag-text-dim)] tabular-nums">{job.processed || 0}/{job.total || "?"} · {job.created || 0} creados · {job.merged || 0} fusionados</span>
          </div>
          <div className="mt-3 h-2 rounded-full bg-[var(--ag-midnight)] overflow-hidden">
            <div className="h-full bg-emerald-500 transition-all duration-500" style={{ width: `${job.status === "done" ? 100 : pct}%` }} />
          </div>
        </div>
      )}

      {/* Vista previa */}
      {preview?.ok && (
        <div className="ag-card p-5 space-y-3" data-testid="sa-dir-rasic-preview-report">
          <div className="flex flex-wrap items-center gap-4 text-sm">
            <strong>{preview.total_rows}</strong><span className="text-[var(--ag-text-dim)] -ml-2">talleres RASIC en «{preview.province}»</span>
            <span className="text-emerald-300 tabular-nums">✓ {preview.counts.ok} nuevos</span>
            <span className="text-[var(--ag-gold)] tabular-nums">⤴ {preview.counts.merged} se fusionarán con perfiles existentes</span>
            <span className="text-[var(--ag-text-dim)] tabular-nums">⊘ {preview.counts.skipped_duplicate} omitidos (NIF repetido en RASIC)</span>
          </div>
          <div className="overflow-x-auto max-h-72 overflow-y-auto border border-[var(--ag-border)] rounded-sm">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--ag-midnight-soft)]">
                <tr className="text-[9px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
                  <th className="text-left px-3 py-2">Negocio</th>
                  <th className="text-left px-3 py-2">Ciudad</th>
                  <th className="text-left px-3 py-2">Sectores</th>
                  <th className="text-left px-3 py-2">NIF</th>
                  <th className="text-left px-3 py-2">Estado</th>
                </tr>
              </thead>
              <tbody>
                {(preview.results || []).slice(0, 100).map((r, i) => (
                  <tr key={`${r.row}-${i}`} className="border-b border-[var(--ag-border)]/40">
                    <td className="px-3 py-1.5">{r.name}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{r.city || "—"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{(r.sectors || []).join(", ") || "—"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)] tabular-nums">{r.tax_id || "—"}</td>
                    <td className={`px-3 py-1.5 ${r.status === "ok" ? "text-emerald-300" : r.status === "merged" ? "text-[var(--ag-gold)]" : "text-[var(--ag-text-dim)]"}`}>
                      {r.status === "ok" ? "✓ Nuevo" : r.status === "merged" ? `⤴ ${r.reason?.slice(0,40)}` : r.reason}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- edit -----
function EditModal({ org, onClose, onSaved }) {
  const [form, setForm] = useState({
    name: org.name || "", description: org.description || "", address: org.address || "",
    city: org.city || "", province: org.province || "", postal_code: org.postal_code || "",
    phone: org.phone || "", whatsapp: org.whatsapp || "", website_url: org.website_url || "",
    notification_email: org.notification_email || "", hours_text: org.hours_text || "",
    sectors: org.sectors || [],
  });
  const [allSectors, setAllSectors] = useState([]);
  const [saving, setSaving] = useState(false);
  useEffect(() => {
    api.get("/catalog/sectors").then(({ data }) => setAllSectors(data.sectors || [])).catch(() => {});
  }, []);
  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleSector = (id) => set("sectors", form.sectors.includes(id) ? form.sectors.filter((s) => s !== id) : [...form.sectors, id]);

  const save = async () => {
    setSaving(true);
    try {
      await api.put(`/superadmin/directory/${org.id}`, form);
      toast.success("Perfil actualizado");
      onSaved();
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error al guardar"); }
    finally { setSaving(false); }
  };

  const Field = ({ label, k, icon: Icon, placeholder }) => (
    <div>
      <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] flex items-center gap-1">
        {Icon && <Icon size={11} />} {label}
      </label>
      <Input value={form[k]} onChange={(e) => set(k, e.target.value)} placeholder={placeholder} className="h-9 mt-1" data-testid={`sa-dir-edit-field-${k}`} />
    </div>
  );

  return (
    <div className="fixed inset-0 z-[70] bg-black/70 backdrop-blur-sm grid place-items-center p-4" onClick={onClose}>
      <div className="ag-card max-w-2xl w-full max-h-[88vh] overflow-y-auto" onClick={(e) => e.stopPropagation()} data-testid="sa-dir-edit-modal">
        <div className="flex items-center justify-between p-5 border-b border-[var(--ag-border)] sticky top-0 bg-[var(--ag-midnight-soft)] z-10">
          <h3 className="font-display font-black text-xl tracking-tight">Editar perfil</h3>
          <button onClick={onClose} className="text-[var(--ag-text-dim)] hover:text-white" aria-label="Cerrar" data-testid="sa-dir-edit-close"><X size={18} /></button>
        </div>
        <div className="p-5 grid grid-cols-1 sm:grid-cols-2 gap-3">
          <Field label="Nombre" k="name" icon={Storefront} />
          <Field label="Teléfono" k="phone" icon={Phone} />
          <Field label="WhatsApp" k="whatsapp" icon={Phone} />
          <Field label="Email" k="notification_email" />
          <Field label="Dirección" k="address" icon={MapPin} />
          <Field label="Ciudad" k="city" icon={MapPin} />
          <Field label="Provincia" k="province" />
          <Field label="Código postal" k="postal_code" />
          <Field label="Web" k="website_url" icon={Globe} />
          <Field label="Horario (texto)" k="hours_text" />
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Descripción</label>
            <textarea value={form.description} onChange={(e) => set("description", e.target.value)} rows={3}
              className="mt-1 w-full px-3 py-2 rounded-sm bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] text-sm"
              data-testid="sa-dir-edit-field-description" />
          </div>
          <div className="sm:col-span-2">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Sectores</label>
            <div className="flex flex-wrap gap-1.5 mt-1.5">
              {allSectors.map((s) => (
                <button key={s.id} type="button" onClick={() => toggleSector(s.id)}
                  className={`px-2.5 py-1 rounded-sm border text-[10px] uppercase tracking-wider transition-colors ${form.sectors.includes(s.id) ? "bg-[var(--ag-gold)] text-black border-transparent font-bold" : "border-[var(--ag-border)] text-[var(--ag-text-dim)] hover:text-white"}`}
                  data-testid={`sa-dir-edit-sector-${s.id}`}>
                  {s.label_es}
                </button>
              ))}
            </div>
          </div>
        </div>
        <div className="p-5 border-t border-[var(--ag-border)] flex justify-end gap-2">
          <Button variant="ghost" onClick={onClose} className="h-10 text-[var(--ag-text-dim)]">Cancelar</Button>
          <Button onClick={save} disabled={saving} className="ag-btn-gold h-10 px-6" data-testid="sa-dir-edit-save">
            {saving ? "Guardando…" : "Guardar cambios"}
          </Button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------- import ---
function ImportCsv({ onImported }) {
  const [csvText, setCsvText] = useState("");
  const [fileName, setFileName] = useState("");
  const [sourceName, setSourceName] = useState("");
  const [report, setReport] = useState(null);
  const [busy, setBusy] = useState(false);
  const fileRef = useRef(null);

  const onFile = (e) => {
    const f = e.target.files?.[0];
    if (!f) return;
    setFileName(f.name);
    if (!sourceName) setSourceName(f.name.replace(/\.csv$/i, ""));
    const reader = new FileReader();
    reader.onload = () => { setCsvText(String(reader.result || "")); setReport(null); };
    reader.readAsText(f, "utf-8");
  };

  const run = async (commit) => {
    if (!csvText.trim()) { toast.error("Selecciona un archivo CSV primero"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/superadmin/directory/import/csv", {
        csv_text: csvText, source_name: sourceName || fileName || "CSV manual", commit,
      });
      if (data.ok === false) { toast.error(data.error || "CSV no válido"); setReport(data); return; }
      setReport(data);
      if (commit) {
        toast.success(`Importados ${data.created} perfiles → cola de revisión`);
        onImported();
      }
    } catch (e) { toast.error(formatErr(e?.response?.data?.detail) || "Error al procesar el CSV"); }
    finally { setBusy(false); }
  };

  const ROW_CLS = {
    ok: "text-emerald-300", flagged_duplicate: "text-orange-300",
    skipped_duplicate: "text-[var(--ag-text-dim)]", error: "text-red-300",
  };
  const ROW_LABEL = { ok: "OK", flagged_duplicate: "Dup?", skipped_duplicate: "Omitido", error: "Error" };

  return (
    <div className="space-y-4">
      <div className="ag-card p-5 space-y-4">
        <div className="text-sm text-[var(--ag-text-dim)] leading-relaxed">
          <p>
            Columnas reconocidas (cabecera flexible · separador <code>;</code>, <code>,</code> o <code>\t</code> · UTF-8 ·{" "}
            <strong className="text-[var(--ag-text)]">mayúsculas/minúsculas y acentos da igual</strong>):
          </p>
          <p className="mt-1.5">
            <span className="text-[var(--ag-text)]/80"><strong>Negocio</strong>:</span>{" "}
            <code className="text-[var(--ag-text)]/80">empresa · nombre · negocio · business · company</code> · <em>(obligatoria)</em>
          </p>
          <p>
            <span className="text-[var(--ag-text)]/80"><strong>Ubicación</strong>:</span>{" "}
            <code className="text-[var(--ag-text)]/80">municipio · ciudad · poblacion · localidad · city · municipality · town</code> · <em>(obligatoria)</em> ·{" "}
            <code className="text-[var(--ag-text)]/80">provincia · comarca · cp · direccion · lat · lng</code>
          </p>
          <p>
            <span className="text-[var(--ag-text)]/80"><strong>Contacto</strong>:</span>{" "}
            <code className="text-[var(--ag-text)]/80">telefono · email · web · whatsapp · instagram · facebook</code>
          </p>
          <p>
            <span className="text-[var(--ag-text)]/80"><strong>Negocio extendido</strong>:</span>{" "}
            <code className="text-[var(--ag-text)]/80">tipo · categoria · servicios · descripcion · horario · valoracion · reseñas</code>
          </p>
          <p className="mt-2">
            Mínimo requerido: <strong className="text-[var(--ag-text)]">empresa + municipio</strong> (o sus equivalentes en inglés:{" "}
            <code className="text-[var(--ag-text)]/80">name + city</code>). Nada se publica sin tu aprobación.
          </p>
        </div>
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Archivo CSV</label>
            <div className="mt-1">
              <input ref={fileRef} type="file" accept=".csv,text/csv" onChange={onFile} className="hidden" data-testid="sa-dir-import-file" />
              <Button variant="outline" onClick={() => fileRef.current?.click()} className="h-10 border-[var(--ag-border)] bg-transparent text-[var(--ag-text)] hover:bg-[var(--ag-midnight-soft)]" data-testid="sa-dir-import-file-btn">
                <UploadSimple size={15} className="mr-2" /> {fileName || "Seleccionar CSV"}
              </Button>
            </div>
          </div>
          <div className="flex-1 min-w-[220px]">
            <label className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)]">Nombre de la fuente (trazabilidad)</label>
            <Input value={sourceName} onChange={(e) => setSourceName(e.target.value)} placeholder="Ej: Listado Cámara Comercio Lleida 2026" className="h-10 mt-1" data-testid="sa-dir-import-source" />
          </div>
          <Button onClick={() => run(false)} disabled={busy || !csvText} className="h-10 px-5 bg-[var(--ag-midnight-soft)] border border-[var(--ag-border)] text-[var(--ag-text)] hover:border-[var(--ag-gold)]/60" data-testid="sa-dir-import-preview">
            {busy ? "Analizando…" : "Analizar (preview)"}
          </Button>
        </div>
      </div>

      {report?.ok && (
        <div className="ag-card p-5 space-y-4" data-testid="sa-dir-import-report">
          <div className="flex flex-wrap items-center gap-4">
            <div className="text-sm"><span className="text-[var(--ag-text-dim)]">Filas:</span> <strong className="tabular-nums">{report.total_rows}</strong></div>
            <div className="text-sm text-emerald-300 tabular-nums">✓ {report.counts.ok} válidos</div>
            <div className="text-sm text-orange-300 tabular-nums">⚑ {report.counts.flagged_duplicate} posibles duplicados</div>
            <div className="text-sm text-[var(--ag-text-dim)] tabular-nums">⊘ {report.counts.skipped_duplicate} omitidos (teléfono duplicado)</div>
            <div className="text-sm text-red-300 tabular-nums">✕ {report.counts.error} errores</div>
            {!report.committed && (report.counts.ok + report.counts.flagged_duplicate) > 0 && (
              <Button onClick={() => run(true)} disabled={busy} className="ag-btn-gold h-10 px-6 ml-auto" data-testid="sa-dir-import-commit">
                {busy ? "Importando…" : `Confirmar importación (${report.counts.ok + report.counts.flagged_duplicate} perfiles)`}
              </Button>
            )}
            {report.committed && (
              <span className="ml-auto text-sm text-emerald-300 inline-flex items-center gap-1.5" data-testid="sa-dir-import-done">
                <CheckCircle size={16} weight="fill" /> Importación completada · {report.created} perfiles en cola de revisión
              </span>
            )}
          </div>
          <div className="overflow-x-auto max-h-80 overflow-y-auto border border-[var(--ag-border)] rounded-sm">
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-[var(--ag-midnight-soft)]">
                <tr className="text-[9px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
                  <th className="text-left px-3 py-2">Fila</th>
                  <th className="text-left px-3 py-2">Negocio</th>
                  <th className="text-left px-3 py-2">Ciudad</th>
                  <th className="text-left px-3 py-2">Sectores</th>
                  <th className="text-center px-3 py-2">Conf.</th>
                  <th className="text-left px-3 py-2">Resultado</th>
                </tr>
              </thead>
              <tbody>
                {(report.results || []).map((r) => (
                  <tr key={r.row} className="border-b border-[var(--ag-border)]/40">
                    <td className="px-3 py-1.5 tabular-nums text-[var(--ag-text-dim)]">{r.row}</td>
                    <td className="px-3 py-1.5">{r.name}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{r.city || "—"}</td>
                    <td className="px-3 py-1.5 text-[var(--ag-text-dim)]">{(r.sectors || []).join(", ") || "—"}</td>
                    <td className="px-3 py-1.5 text-center tabular-nums">{r.confidence ?? "—"}</td>
                    <td className={`px-3 py-1.5 ${ROW_CLS[r.status]}`}>{ROW_LABEL[r.status]}{r.reason ? ` · ${r.reason}` : ""}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------- batches --
function BatchesList() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  useEffect(() => {
    api.get("/superadmin/directory/imports")
      .then(({ data }) => setItems(data.items || []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);
  return (
    <div className="ag-card overflow-x-auto" data-testid="sa-dir-batches">
      {loading ? (
        <div className="p-8 text-center text-sm text-[var(--ag-text-dim)]">Cargando…</div>
      ) : items.length === 0 ? (
        <div className="p-10 text-center text-sm text-[var(--ag-text-dim)]">Aún no hay importaciones.</div>
      ) : (
        <table className="w-full text-sm">
          <thead>
            <tr className="text-[10px] uppercase tracking-[0.15em] text-[var(--ag-text-dim)] border-b border-[var(--ag-border)]">
              <th className="text-left px-4 py-3">Fecha</th>
              <th className="text-left px-4 py-3">Fuente</th>
              <th className="text-right px-4 py-3">Filas</th>
              <th className="text-right px-4 py-3">Creados</th>
              <th className="text-right px-4 py-3">Omitidos</th>
              <th className="text-right px-4 py-3">Errores</th>
              <th className="text-left px-4 py-3 hidden md:table-cell">Por</th>
            </tr>
          </thead>
          <tbody>
            {items.map((b) => (
              <tr key={b.id} className="border-b border-[var(--ag-border)]/50">
                <td className="px-4 py-3 text-[var(--ag-text-dim)]">{(b.created_at || "").slice(0, 16).replace("T", " ")}</td>
                <td className="px-4 py-3 font-semibold">{b.source_name}</td>
                <td className="px-4 py-3 text-right tabular-nums">{b.total_rows}</td>
                <td className="px-4 py-3 text-right tabular-nums text-emerald-300">{b.created}</td>
                <td className="px-4 py-3 text-right tabular-nums text-[var(--ag-text-dim)]">{b.skipped_duplicates}</td>
                <td className="px-4 py-3 text-right tabular-nums text-red-300">{b.errors}</td>
                <td className="px-4 py-3 hidden md:table-cell text-[var(--ag-text-dim)] text-xs">{b.created_by}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}
